import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTH_KICKED_KEY } from '../../request';
import { useAuthStore } from '../../store/auth';
import { usePhoneStore } from '../../store/phone';

export interface WsEvent {
  type: string;
  data?: any;
}

/** 心跳周期（ms）：每 30s 发一次 ping，服务端 readPump 回 pong */
const HEARTBEAT_MS = 30000;
/** 失活判定（ms）：超过该时长未收到任何消息（含 pong）即判定连接已死并主动断开重连。
 * 休眠/断网/中间设备静默丢连接时 onclose 不会触发，坐席侧会「假在线」——发送失败而界面无感，
 * 只能靠心跳兜底（正常有心跳时最长静默 ≈ HEARTBEAT_MS）。 */
const STALE_MS = 75000;
/** 重连退避：3s 起指数递增至 30s，叠加随机抖动防多坐席同时重连打爆后端 */
const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;

/**
 * force_logout 收尾的一次性护栏：登录态被顶下线时，BasicLayout / QuickDialBar / 工作台
 * 各自持有的连接都会被服务端断开并各收到一帧，只处理一次（否则重复弹窗、重复跳转）。
 */
let kickedHandled = false;

/**
 * 被顶下线收尾（坐席账号在别处登录）：先注销软电话释放分机，再清登录态带提示回登录页。
 * 服务端已把该会话的 token 判为失效，这里不做任何重试——重连只会拿到 401 code=4011。
 */
function handleForcedLogout() {
  if (kickedHandled) return;
  kickedHandled = true;
  // 立即注销 SIP（分机必须独占，不能等旧端自然超时）；注销异常绝不阻断登出（登出才是本路径的主任务）
  try {
    void Promise.resolve(usePhoneStore.getState().unregister()).catch(() => undefined);
  } catch {
    /* 软电话模块不可用（如未接线/开发态替换实现）：跳过注销，继续登出 */
  }
  try {
    sessionStorage.setItem(AUTH_KICKED_KEY, '1');
  } catch {
    /* 存储不可用：仅少一条提示，不阻断登出 */
  }
  useAuthStore.getState().logout();
  window.location.href = '/login';
}

/**
 * 管理台 WebSocket：/ws/admin?token=
 * 事件：call_ringing / call_answered / call_hangup / cdr_written / agent_state /
 *       queue_update / chat_new / chat_message / chat_closed / force_logout（被顶下线）
 * 断线自动重连（指数退避）+ 心跳保活（30s ping / 75s 无消息判死）。
 */
export function useAdminWs(onEvent?: (e: WsEvent) => void) {
  const token = useAuthStore((s) => s.token);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WsEvent | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const hbRef = useRef<ReturnType<typeof setInterval>>();
  // 连续重连次数（onopen 归零）：仅用于退避时长计算
  const attemptRef = useRef(0);
  // 最近一次收到任意消息的时间：心跳线程据此判定连接是否已死
  const lastMsgRef = useRef(0);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const send = useCallback((obj: any): boolean => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!token) return;
    let disposed = false;

    const stopHeartbeat = () => {
      if (hbRef.current) {
        clearInterval(hbRef.current);
        hbRef.current = undefined;
      }
    };

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${proto}://${window.location.host}/ws/admin?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        lastMsgRef.current = Date.now();
        setConnected(true);
        stopHeartbeat();
        hbRef.current = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          // 静默超时：连接已死但未触发 onclose，主动断开走重连
          if (Date.now() - lastMsgRef.current > STALE_MS) {
            ws.close();
            return;
          }
          ws.send(JSON.stringify({ type: 'ping' }));
        }, HEARTBEAT_MS);
      };
      ws.onclose = () => {
        setConnected(false);
        stopHeartbeat();
        wsRef.current = null;
        if (!disposed) {
          const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attemptRef.current) + Math.random() * 800;
          attemptRef.current += 1;
          timerRef.current = setTimeout(connect, delay);
        }
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        lastMsgRef.current = Date.now();
        try {
          const msg = JSON.parse(ev.data) as WsEvent;
          // 心跳回包不是业务事件：只刷新存活时间，不驱动 lastEvent/事件日志
          if (msg.type === 'pong') return;
          // 服务端顶下线（坐席在别处登录）：断开重连链，直接登出（重连只会被 401 拒绝）
          if (msg.type === 'force_logout') {
            disposed = true;
            stopHeartbeat();
            ws.close();
            handleForcedLogout();
            return;
          }
          setLastEvent(msg);
          onEventRef.current?.(msg);
        } catch {
          /* 非 JSON 消息忽略 */
        }
      };
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(timerRef.current);
      stopHeartbeat();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [token]);

  return { connected, send, lastEvent };
}
